// src/components/Layout.jsx
import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../App";
import { logoutUser } from "../services/firebase";

const NAV_ITEMS = [
  { to: "/",         label: "Dashboard",  icon: GridIcon    },
  { to: "/weight",   label: "Weight",     icon: TrendIcon   },
  { to: "/food",     label: "Food",       icon: ForkIcon    },
  { to: "/exercise", label: "Exercise",   icon: BoltIcon    },
  { to: "/meals",    label: "Meal Plan",  icon: CalendarIcon},
  { to: "/suggest",  label: "Suggest",   icon: SparkIcon   },
  { to: "/grocery",  label: "Grocery",   icon: BagIcon     },
  { to: "/body-fat", label: "Body Fat",  icon: BodyIcon    },
];

export default function Layout() {
  const { profile, user } = useApp();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logoutUser();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0d1117] flex">

      {/* ── Sidebar (desktop ≥ lg) ───────────────────────────────────── */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#0d1117] border-r border-[#1f2a3c] fixed h-full z-20">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-[#1f2a3c]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#ff7c0a] rounded-md flex items-center justify-center flex-shrink-0">
              <BoltIcon className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-widest text-slate-100 uppercase"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              CutTracker
            </span>
          </div>
          {user && (
            <div className="mt-2 text-xs text-slate-600 truncate">{user.email}</div>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? "bg-[#ff7c0a]/15 text-[#ff7c0a] border border-[#ff7c0a]/20"
                    : "text-slate-400 hover:text-slate-200 hover:bg-[#161d28]"
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Profile chip + logout */}
        <div className="px-3 py-4 border-t border-[#1f2a3c]">
          {profile && (
            <div className="px-3 py-2 mb-2 bg-[#111820] rounded-lg">
              <div className="text-sm font-semibold text-slate-200 truncate">{profile.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {profile.calorie_target?.toLocaleString()} cal · {profile.protein_target}g protein
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                       text-slate-500 hover:text-red-400 hover:bg-red-900/10 transition-all duration-150"
          >
            <LogoutIcon className="w-4 h-4" />
            {loggingOut ? "Signing out…" : "Sign Out"}
          </button>
        </div>
      </aside>

      {/* ── Mobile top bar ────────────────────────────────────────────── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#0d1117]/95 backdrop-blur
                      border-b border-[#1f2a3c] flex items-center justify-between px-4 h-12">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#ff7c0a] rounded-md flex items-center justify-center">
            <BoltIcon className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-base font-bold tracking-widest text-slate-100 uppercase"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            CutTracker
          </span>
        </div>
        {profile && (
          <span className="text-xs text-slate-500">{profile.name}</span>
        )}
      </div>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <main className="flex-1 lg:ml-60 pb-20 lg:pb-8 pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-6 lg:px-8 lg:py-8 animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* ── Mobile bottom tab bar ─────────────────────────────────────── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0d1117]/95 backdrop-blur
                      border-t border-[#1f2a3c] flex">
        {NAV_ITEMS.slice(0, 5).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 gap-0.5
               text-[10px] font-medium transition-colors duration-150 ${
                isActive ? "text-[#ff7c0a]" : "text-slate-500"
               }`
            }
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          onClick={() => setDrawerOpen(true)}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5
                     text-[10px] font-medium text-slate-500"
        >
          <MoreIcon className="w-5 h-5" />
          <span>More</span>
        </button>
      </nav>

      {/* ── Mobile overflow drawer ────────────────────────────────────── */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <div className="relative bg-[#161d28] border-t border-[#1f2a3c] rounded-t-2xl p-4 animate-slide-up">
            <div className="w-10 h-1 bg-[#263347] rounded-full mx-auto mb-4" />
            <div className="space-y-1 mb-4">
              {NAV_ITEMS.slice(5).map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive ? "bg-[#ff7c0a]/15 text-[#ff7c0a]" : "text-slate-300 hover:bg-[#1f2a3c]"
                    }`
                  }
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </NavLink>
              ))}
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm
                         text-red-400 hover:bg-red-900/20 transition-all"
            >
              <LogoutIcon className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────
function GridIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth="2"/>
    <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth="2"/>
    <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth="2"/>
    <rect x="14" y="14" width="7" height="7" rx="1" strokeWidth="2"/>
  </svg>;
}
function TrendIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="16 7 22 7 22 13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>;
}
function ForkIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M3 2v6c0 1.1.9 2 2 2h2a2 2 0 002-2V2M7 10v12M15 2c0 0 4 2 4 6s-4 6-4 6V2z"/>
  </svg>;
}
function BoltIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>
  </svg>;
}
function CalendarIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8"  y1="2" x2="8"  y2="6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="3"  y1="10" x2="21" y2="10" strokeWidth="2"/>
  </svg>;
}
function SparkIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
  </svg>;
}
function BagIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
  </svg>;
}
function BodyIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="5" r="2" strokeWidth="2"/>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M12 7v6m0 0l-3 5m3-5l3 5M9 12H6m6 0h3"/>
  </svg>;
}
function LogoutIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
  </svg>;
}
function MoreIcon({ className }) {
  return <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5"  cy="12" r="1.5"/>
    <circle cx="12" cy="12" r="1.5"/>
    <circle cx="19" cy="12" r="1.5"/>
  </svg>;
}
